import React from "react"
import { PageDrawerAction } from "src/components/ui/PageDrawerAction"
import { Icons } from "src/components/ui/Icons"
import { useActions, useAppState, useReaction } from "src/state"
import { 
    UserFragment,
    Users_Insert_Input,
    UpdateUserMutationVariables,
    User_Roles_Enum,
    User_Type_Enum,
    User_Status_Enum
} from "src/state/gql/_types"
import { FormDrawer } from "src/components/forms/FormDrawer"
import { Loader } from "@mantine/core"
import { 
    SuperAdminVenueUserCreateFragment, 
    SuperAdminVenueUserCreateSchema, 
    SuperAdminVenueUserUpdateFragment, 
    SuperAdminVenueUserUpdateSchema 
} from "src/components/superadmin/venues/users/SuperAdminVenueUserSchemas"
import { extractCurrentItemData } from "src/utils/forms"
import { uuidToVibrantHexColor } from "src/utils/calendar"

type FormPayloadType = SuperAdminVenueUserCreateFragment

let initialState: FormPayloadType = {
    role: User_Roles_Enum.Administrator,
    type: User_Type_Enum.Operations,
    email: '',
    first_name: '',
    last_name: '',
    phone: '',  
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    color: uuidToVibrantHexColor(crypto.randomUUID()),
    timezone: 'America/New_York'
};

export const CreateSuperAdminVenueUserDrawer: React.FC<{onClose():void}> = ({ onClose }) => {

    const { createItem, updateItem, deleteItem } = useActions().users;
    const { loading, errors } = useAppState().users;
    const { currentItemId: currentTenantId } = useAppState().tenants;
    
    let currentItemId = null;
    let currentItemData: FormPayloadType | null = null;

    const handleCreate = (payload: Users_Insert_Input) => {
        return createItem({ tenant_id: currentTenantId, ...payload })
    }

    return (
        <>
            <FormDrawer<FormPayloadType, UserFragment, string>
                autoOpen={false}
                formSchema={SuperAdminVenueUserCreateSchema}
                createAction={handleCreate}
                updateAction={updateItem}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialState}
                currentItemData={currentItemData}
                onClose={onClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const CreateSuperAdminVenueUserAction = () => {
    return (
        <PageDrawerAction<UserFragment>
            title='Add User'
            action={CreateSuperAdminVenueUserDrawer}
            icon={Icons.add}
        />
    )
}

let initialStateUpdate: SuperAdminVenueUserUpdateFragment = {
    status: User_Status_Enum.Active,
    role: User_Roles_Enum.Administrator,
    type: User_Type_Enum.Operations,
    email: '',
    first_name: '',
    last_name: '',
    phone: '',  
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    color: "",
    timezone: 'America/New_York'
};

export const UpdateSuperAdminVenueUserDrawer: React.FC<{onClose():void,deselect:boolean}> = ({ onClose, deselect = true }) => {

    
    const { updateItem, deleteItem, deselectItem } = useActions().users;
    const { currentItem, loading, errors } = useAppState().users;

    if (loading || !currentItem) return <Loader />

    let currentItemId = null;
    let currentItemData: SuperAdminVenueUserUpdateFragment | null = null;
    if (currentItem) {
        currentItemId = currentItem.id;
        currentItemData = extractCurrentItemData<SuperAdminVenueUserUpdateFragment>(initialStateUpdate, currentItem);
    }

    const handleClose = () => {
        if(deselect) {
            deselectItem()
        }
        onClose()
    }

    const handleUpdate = (payload: UpdateUserMutationVariables) => {
        return updateItem({
            id: payload.id,
            _set: {
                ...payload._set
            }
        })
    }

    return (
        <>
            <FormDrawer<SuperAdminVenueUserUpdateFragment, UserFragment, string>
                autoOpen={false}
                formSchema={SuperAdminVenueUserUpdateSchema}
                updateAction={handleUpdate}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialStateUpdate}
                currentItemData={currentItemData}
                onClose={handleClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const UpdateSuperAdminVenueUserAction = () => {

    const reaction = useReaction()
    const { deselectItem } = useActions().users;
    const [opened, setOpened] = React.useState(false);

    React.useEffect(() => reaction(
        (state) => state.users.currentItemId,
        (selectedID) => {
            setOpened(!!selectedID)
        }, { immediate: false }
    ))

    const handleClose = () => {
        deselectItem()
    }

    return (
        <PageDrawerAction<UserFragment>
            title='Edit User'
            action={UpdateSuperAdminVenueUserDrawer}
            icon={Icons.edit}
            startOpened={opened}
            onClose={handleClose}
            hidden
        />
    )
}

export const EditSuperAdminVenueUserAction = () => {
    return (
        <PageDrawerAction<UserFragment>
            title='Edit User'
            action={UpdateSuperAdminVenueUserDrawer}
            icon={Icons.edit}
            props={{
                deselect: false
            }}
        />
    )
}